import React, { useEffect, useMemo, useState } from "react";
import {
  fetchDictionaryWords,
  DictionaryWord,
  deleteWordFromDictionary,
} from "./api/dictionaryApi";
import EditWordModal from "../dictionary/EditWordModal";
import { FaPlus } from "react-icons/fa";

interface DictionaryViewProps {
  language: string;
  refreshTrigger?: number; // Optional prop to trigger refresh
  onRequestAddWord?: () => void;
}

const DictionaryView: React.FC<DictionaryViewProps> = ({
  language,
  refreshTrigger,
  onRequestAddWord,
}) => {
  const [words, setWords] = useState<DictionaryWord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingWord, setEditingWord] = useState<DictionaryWord | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const nextReviewFormatter = useMemo(() => new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }), []);

  const loadWords = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[DictionaryView] Fetching words...');
      const fetchedWords = await fetchDictionaryWords(language);
      console.log('[DictionaryView] Fetched words:', fetchedWords);
      setWords(fetchedWords);
    } catch (error) {
      console.error("Failed to load dictionary words:", error);
      setError("Failed to load dictionary words. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('[DictionaryView] Loading words for language:', language);
    loadWords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, refreshTrigger]); // Add refreshTrigger to dependencies

  const handleDeleteWord = async (wordId: number) => {
    if (!window.confirm("Are you sure you want to delete this word?")) {
      return;
    }

    try {
      await deleteWordFromDictionary(wordId);
      setWords(words.filter((word) => word.user_marked_word_id !== wordId));
    } catch (error) {
      console.error("Failed to delete word:", error);
      alert("Failed to delete word. Please try again.");
    }
  };

  const handleOpenEdit = (word: DictionaryWord) => {
    setEditingWord(word);
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setEditingWord(null);
  };

  const handleWordUpdated = (updatedWord: DictionaryWord) => {
    setWords((prevWords) =>
      prevWords.map((word) =>
        word.user_marked_word_id === updatedWord.user_marked_word_id
          ? updatedWord
          : word
      )
    );
    handleCloseEdit();
  };

  const getIntervalBadgeColor = (word: DictionaryWord): string => {
    if (word.lapses >= 3) return "badge-error";
    if (word.interval <= 1) return "badge-warning";
    if (word.interval <= 6) return "badge-info";
    if (word.interval <= 15) return "badge-primary";
    return "badge-success";
  };

  const formatRelativeDays = (dateString: string | null): string => {
    if (!dateString) return "due now";
    const target = new Date(dateString);
    if (Number.isNaN(target.getTime())) return "unknown";

    const diffMs = target.getTime() - Date.now();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (Math.abs(diffDays) < 1) {
      const diffHours = Math.round(diffMs / (1000 * 60 * 60));
      if (Math.abs(diffHours) < 1) {
        const diffMinutes = Math.round(diffMs / (1000 * 60));
        return nextReviewFormatter.format(diffMinutes, "minute");
      }
      return nextReviewFormatter.format(diffHours, "hour");
    }
    return nextReviewFormatter.format(diffDays, "day");
  };

  const formatDateTooltip = (dateString: string | null): string => {
    if (!dateString) return "Not reviewed yet";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <div className="skeleton h-32 w-full bg-base-300"></div>
        <div className="skeleton h-32 w-full bg-base-300"></div>
        <div className="skeleton h-32 w-full bg-base-300"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-error">
        <p>{error}</p>
        <button onClick={loadWords} className="btn btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div className="p-6 text-center text-base-content/70 space-y-4">
        <div>
          <p className="font-medium">No saved words yet</p>
          <p className="text-sm text-base-content/60 mt-1">
            Add something manually or tap highlighted words during chat.
          </p>
        </div>
        {onRequestAddWord && (
          <button
            onClick={onRequestAddWord}
            className="btn btn-primary btn-sm gap-2"
          >
            <FaPlus /> Add first word
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-base-content/60">
            Saved words
          </p>
          <p className="text-sm font-medium text-base-content">
            {words.length} {words.length === 1 ? "word" : "words"}
          </p>
        </div>
        {onRequestAddWord && (
          <button
            onClick={onRequestAddWord}
            className="btn btn-primary btn-sm gap-2 shrink-0"
          >
            <FaPlus />
            Add word
          </button>
        )}
      </div>

      <div className="space-y-3">
        {words.map((word) => (
          <div
            key={word.user_marked_word_id}
            className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="card-body p-4">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold">{word.word_base_form}</h3>
                    <span
                      className={`badge ${getIntervalBadgeColor(word)}`}
                      title={`Interval: ${word.interval} day${word.interval === 1 ? "" : "s"}`}
                    >
                      {word.interval}d
                    </span>
                    <span className="badge badge-outline" title="Ease factor">
                      EF {word.ease_factor.toFixed(2)}
                    </span>
                    <span className="badge badge-ghost" title="Success streak">
                      🔁 {word.success_streak}
                    </span>
                    {word.lapses > 0 && (
                      <span className="badge badge-error badge-outline" title="Times failed">
                        {word.lapses} lapse{word.lapses === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-base-content/70">
                    {word.word_translation} ({word.grammar})
                  </p>

                  <div className="grid grid-cols-1 gap-1 text-xs text-base-content/60">
                    <span title={formatDateTooltip(word.next_review_at)}>
                      Next review: {formatRelativeDays(word.next_review_at)}
                    </span>
                    <span title={formatDateTooltip(word.last_reviewed_at)}>
                      Last review:{" "}
                      {word.last_reviewed_at ? formatRelativeDays(word.last_reviewed_at) : "not yet"}
                    </span>
                  </div>

                  <div className="mt-2 space-y-1">
                    <p className="text-sm italic text-base-content/80">{word.example_sentence}</p>
                    <p className="text-xs text-base-content/60">
                      {word.example_sentence_translation}
                    </p>
                    {word.user_comment && (
                      <p className="text-xs text-base-content/70 mt-2">
                        Notes: {word.user_comment}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2 items-end shrink-0">
                  <button
                    onClick={() => handleOpenEdit(word)}
                    className="btn btn-outline btn-sm"
                    title="Edit word"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteWord(word.user_marked_word_id)}
                    className="btn btn-ghost btn-sm btn-circle"
                    title="Delete word"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <EditWordModal
        isOpen={isEditModalOpen}
        word={editingWord}
        onClose={handleCloseEdit}
        onWordUpdated={handleWordUpdated}
      />
    </div>
  );
};

export default DictionaryView;
